/**
 * TRITAL Care® Skills Checklists — production data registry.
 *
 * Source taxonomy mirrors the rehabilitycare.com skills-checklist catalog,
 * rewritten as TRITAL Care branded competency banks.
 *
 * Rating scale (consistent across every checklist):
 *   1 = No Experience
 *   2 = Need Training
 *   3 = Able to perform with supervision
 *   4 = Able to perform independently
 *
 * Shape:
 *   CHECKLISTS[type] = {
 *     label, blurb,
 *     items: [
 *       { slug, title, category, summary,
 *         sections: [{ title, questions: [string, ...] }] }
 *     ]
 *   }
 */

/* ---------- Reusable section helpers ---------- */
const PROFESSIONAL_PRACTICE = {
  title: "Professional Practice & Documentation",
  questions: [
    "Charts within shift on EMR (Epic, Cerner, Meditech, or equivalent) using approved abbreviations only",
    "Adheres to HIPAA, patient-rights, and informed-consent procedures",
    "Completes hand-off communication using SBAR",
    "Identifies patients using two unique identifiers prior to every intervention",
    "Reports incidents, errors, and near-misses through facility quality system",
    "Maintains current BLS / ACLS / PALS / NRP as required by role",
  ],
};

const INFECTION_CONTROL = {
  title: "Infection Prevention & Safety",
  questions: [
    "Performs hand hygiene per WHO 5-moments and CDC guidelines",
    "Selects and applies appropriate PPE for standard / contact / droplet / airborne precautions",
    "Identifies isolation requirements and signs out rooms correctly",
    "Disposes of sharps and biohazardous waste per OSHA standards",
    "Recognizes and reports CLABSI / CAUTI / SSI / VAP risk factors",
    "Adheres to MRSA / C. diff / TB / COVID precautions and respirator fit testing",
  ],
};

const PATIENT_SAFETY = {
  title: "Patient Safety & Risk Management",
  questions: [
    "Performs fall-risk screening (Morse / Hester Davis / Humpty Dumpty as applicable)",
    "Implements two-RN medication-verification for high-alert drugs",
    "Uses bed alarms, sitters, and de-escalation techniques appropriately",
    "Recognizes early-warning signs and escalates per rapid-response criteria",
    "Documents and reports skin breakdown, restraints, and elopement events",
    "Performs medication reconciliation at admission, transfer, and discharge",
  ],
};

/* ============================================================
 *  NURSE CHECKLISTS (50+)
 * ============================================================ */
const nurse = [
  {
    slug: "administrative-rn",
    title: "Administrative Registered Nurse",
    category: "Leadership",
    summary: "Department-level administrative RN — staffing, throughput, and quality oversight.",
    sections: [
      {
        title: "Operations & Throughput",
        questions: [
          "Manages daily acuity-based staffing assignments",
          "Coordinates admissions, transfers, and discharges with bed management",
          "Reviews productivity and HPPD reports against targets",
          "Conducts daily safety huddles and tier-board reviews",
          "Oversees census flexing and float-pool requests",
          "Resolves real-time staffing or equipment escalations",
        ],
      },
      {
        title: "Quality, Compliance & Regulatory",
        questions: [
          "Leads root-cause analysis (RCA) for sentinel events",
          "Tracks unit-level CMS, Joint Commission, and CDC measures",
          "Manages CAUTI, CLABSI, and falls reduction bundles",
          "Audits charting compliance and timely documentation",
          "Coordinates Joint Commission survey readiness",
          "Reviews patient-experience scores (HCAHPS) with action plans",
        ],
      },
      {
        title: "Staff Leadership & HR",
        questions: [
          "Conducts performance evaluations and corrective action",
          "Leads onboarding and competency validation for new hires",
          "Mediates interdisciplinary conflict and patient complaints",
          "Plans staff schedules and manages PTO requests",
          "Coaches charge nurses and emerging leaders",
          "Maintains preceptor program and orientation pathways",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "adon-don",
    title: "ADON / DON",
    category: "Leadership",
    summary: "Assistant / Director of Nursing for SNF, LTC, and post-acute facilities.",
    sections: [
      {
        title: "Regulatory & Survey Readiness",
        questions: [
          "Maintains state survey readiness — F-tags, POC management",
          "Manages incident reporting and self-reportable events to state",
          "Oversees QAPI program and PIP projects",
          "Coordinates clinical audits and chart compliance",
          "Reviews MDS accuracy and PDPM compliance",
          "Maintains current knowledge of CMS conditions of participation",
        ],
      },
      {
        title: "Clinical Operations",
        questions: [
          "Oversees medication administration accuracy and pharmacy partnership",
          "Manages infection-prevention program and reportable diseases",
          "Coordinates wound-care, fall-prevention, and restraint reduction programs",
          "Reviews resident-acuity and care-plan accuracy",
          "Leads root-cause analysis for adverse events",
          "Coordinates care-transition with hospital partners",
        ],
      },
      {
        title: "Workforce Management",
        questions: [
          "Builds nursing schedules and PRN coverage plan",
          "Conducts hiring, retention, and corrective-action workflows",
          "Manages clinical orientation and competency validation",
          "Oversees CNA, LPN, and RN education programs",
          "Liaises with payroll, HR, and credentialing",
          "Mentors charge nurses and unit managers",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "ambulatory",
    title: "Ambulatory",
    category: "Outpatient",
    summary: "Outpatient / clinic RN across primary care, specialty, and urgent care.",
    sections: [
      {
        title: "Clinic Workflow & Triage",
        questions: [
          "Performs telephone triage using approved algorithms",
          "Rooms patients efficiently with vitals and chief-complaint capture",
          "Schedules follow-ups, referrals, and prior authorizations",
          "Maintains panel-management for chronic disease populations",
          "Educates patients on self-care, medication, and lifestyle",
          "Participates in quality-measure (HEDIS, MIPS) workflow",
        ],
      },
      {
        title: "Procedural Skills",
        questions: [
          "Administers IM / SubQ / ID injections and vaccines",
          "Performs venipuncture, IV starts, and POC testing",
          "Assists with minor in-clinic procedures (biopsy, I&D, casting)",
          "Performs EKGs, spirometry, and ankle-brachial index",
          "Supports immunotherapy and infusion-clinic workflows",
          "Manages port access and central-line dressing changes",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "cardiac-echo",
    title: "Cardiac Echo",
    category: "Cardiovascular",
    summary: "Echocardiography — TTE / TEE / stress.",
    sections: [
      {
        title: "Echocardiography Acquisition",
        questions: [
          "TTE protocol — parasternal, apical, subcostal, suprasternal views",
          "Doppler technique — pulsed, continuous, color, tissue Doppler",
          "Stress echo (treadmill, dobutamine) acquisition and interpretation support",
          "Bubble study and contrast-enhanced echo",
          "Strain imaging and 3D acquisition",
          "Pediatric / congenital echo basics (where in scope)",
        ],
      },
      {
        title: "TEE & Procedural Support",
        questions: [
          "TEE probe insertion assist, intra-procedural imaging",
          "Conscious sedation monitoring during TEE",
          "Probe disinfection and high-level reprocessing",
          "Structural-heart procedural imaging (TAVR, MitraClip, LAA)",
          "Post-procedure recovery monitoring",
          "Emergency TEE for hemodynamic instability",
        ],
      },
      {
        title: "Reporting & Quality",
        questions: [
          "Generates measurement set per ASE guidelines",
          "Identifies critical findings and notifies ordering provider",
          "Maintains image quality, optimization, and labeling",
          "Participates in continuous quality improvement",
          "Maintains ARDMS / CCI credentialing and CME",
          "Uses worklist / PACS and structured reporting tools",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "cardiac-echocardiography",
    title: "Cardiac Echocardiography",
    category: "Cardiovascular",
    summary: "Comprehensive echocardiography across adult populations.",
    sections: [
      {
        title: "Adult Comprehensive Echo",
        questions: [
          "Complete adult echocardiogram per ASE protocol",
          "Quantitative assessment of LV/RV systolic and diastolic function",
          "Valve disease evaluation including stenosis and regurgitation grading",
          "Pulmonary pressures and right-heart hemodynamics",
          "Wall-motion analysis post-MI / ischemia",
          "Pericardial effusion and tamponade physiology",
        ],
      },
      {
        title: "Specialty Studies",
        questions: [
          "Stress echo (treadmill, dobutamine) acquisition and reporting",
          "Bubble / saline contrast for shunt detection",
          "Strain imaging and 3D echocardiography",
          "Cardiac transplant surveillance",
          "Endocarditis assessment and follow-up",
          "Congenital heart disease in adults",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "case-management-utilization",
    title: "Case Management / Utilization Review",
    category: "Care Coordination",
    summary: "Hospital and post-acute case management & utilization review.",
    sections: [
      {
        title: "Utilization & Status",
        questions: [
          "Applies InterQual or MCG criteria for admission and continued stay",
          "Manages observation vs inpatient status determinations",
          "Communicates with payers, performs concurrent review",
          "Submits appeals and peer-to-peer reviews when denied",
          "Tracks length of stay and avoidable days",
          "Documents medical necessity per Two-Midnight Rule",
        ],
      },
      {
        title: "Discharge Planning",
        questions: [
          "Coordinates SNF, IRF, LTACH, hospice, and home-health placements",
          "Arranges DME and home infusion services",
          "Conducts patient and family discharge education",
          "Manages transportation and post-discharge follow-up",
          "Coordinates social-work referrals and behavioral-health placement",
          "Maintains relationships with post-acute partners",
        ],
      },
      {
        title: "Population Management",
        questions: [
          "Identifies high-risk readmission patients",
          "Coordinates transitions-of-care programs",
          "Reviews chronic-disease management plans",
          "Participates in multidisciplinary rounds",
          "Tracks outcomes and reports to steering committees",
          "Supports value-based-care contract performance",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "cath-lab-interventional-radiology",
    title: "Cath Lab / Interventional Radiology",
    category: "Procedural",
    summary: "Combined cardiac cath / IR procedural nursing.",
    sections: [
      {
        title: "Procedural Support",
        questions: [
          "STEMI activation and door-to-balloon ownership",
          "Sheath insertion / removal and hemostasis (manual, devices)",
          "Moderate sedation monitoring with rescue-airway readiness",
          "Coronary diagnostic / interventional support — PCI, IVUS, FFR, atherectomy",
          "EP procedures — ablation, device implant, tilt-table",
          "IR procedures — embolization, biopsy, drainage, vascular access",
        ],
      },
      {
        title: "Hemodynamics & Critical Events",
        questions: [
          "Arterial line monitoring and pressure transduction",
          "Manages contrast-induced nephropathy prevention",
          "Recognizes and treats coronary perforation, dissection, no-reflow",
          "Activates code blue and IABP / Impella escalation",
          "Manages anticoagulation (heparin, bivalirudin, ACT monitoring)",
          "Performs post-procedure neurovascular checks",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "critical-care",
    title: "Critical Care",
    category: "Critical Care",
    summary: "Adult critical-care nursing across medical, surgical, and cardiac ICU.",
    sections: [
      {
        title: "Hemodynamic & Cardiovascular",
        questions: [
          "Arterial line, CVP, and pulmonary artery (Swan) monitoring",
          "Vasoactive titration — norepinephrine, epinephrine, vasopressin, dobutamine",
          "Post-cardiac arrest and targeted-temperature management",
          "Recognition and management of shock states",
          "IABP and Impella nursing care",
          "Cardiac output / SVO2 assessment",
        ],
      },
      {
        title: "Respiratory & Airway",
        questions: [
          "Mechanical ventilation modes — AC, SIMV, PSV, APRV",
          "ARDS protocol including prone positioning and low-Vt",
          "Daily SAT / SBT and ABCDEF bundle compliance",
          "Endotracheal-tube care, suctioning, and rescue airway",
          "Tracheostomy care and weaning",
          "ECMO awareness and circuit safety",
        ],
      },
      {
        title: "Renal, Neuro & Multisystem",
        questions: [
          "CRRT / SLED / intermittent dialysis nursing",
          "ICP monitoring, EVD management, and neuro assessment",
          "Sepsis bundle execution",
          "Glycemic control protocols",
          "End-of-life and palliative-care transitions",
          "Therapeutic plasma exchange",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "cvor",
    title: "CVOR",
    category: "Surgical",
    summary: "Cardiovascular operating room — CABG, valve, vascular.",
    sections: [
      {
        title: "Cardiac Surgical Setup",
        questions: [
          "Sterile-field setup for CABG, valve replacement, and TAVR",
          "Cannulation, bypass, and cross-clamp coordination with perfusionist",
          "Off-pump CABG and beating-heart stabilizer use",
          "Endoscopic vein / artery harvest assist",
          "VAD and ECMO insertion support",
          "Pediatric / congenital cardiac case awareness",
        ],
      },
      {
        title: "Vascular Surgery",
        questions: [
          "Open AAA repair and aortic dissection cases",
          "Carotid endarterectomy and bypass procedures",
          "Endovascular AAA / TEVAR / EVAR",
          "Fem-pop and lower-extremity bypass",
          "Vascular access surgery — fistula, graft",
          "Trauma vascular cases and damage control",
        ],
      },
      {
        title: "Counts, Specimens & Hemostasis",
        questions: [
          "Surgical counts per AORN guidelines",
          "Specimen handling and chain-of-custody",
          "Hemostasis agents and topical thrombin",
          "Cell-saver and autotransfusion management",
          "Massive transfusion protocol activation",
          "Sterile-cooling and cardiac arrest in OR",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "dialysis-nursing",
    title: "Dialysis Nursing",
    category: "Renal",
    summary: "Acute and chronic hemodialysis nursing.",
    sections: [
      {
        title: "Hemodialysis Operations",
        questions: [
          "HD machine setup, prime, and disinfection (Fresenius, NxStage, Tablo)",
          "Cannulation (buttonhole, rope-ladder) of fistula / graft",
          "Central venous catheter access and dressing care",
          "Heparin / regional anticoagulation administration",
          "Dialysate composition and bath selection",
          "Reuse / reprocessing and water-quality testing (AAMI)",
        ],
      },
      {
        title: "Patient Management",
        questions: [
          "Intradialytic hypotension and cramping management",
          "Air embolism, hemolysis, and dialyzer reactions response",
          "Vascular-access surveillance and recirculation studies",
          "Adequacy monitoring — Kt/V, URR",
          "Anemia, bone-mineral, and ESRD pharmacotherapy",
          "Disaster preparedness and water-shortage protocols",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "dialysis-clinical",
    title: "Dialysis Clinical",
    category: "Renal",
    summary: "Dialysis Clinical Manager / Charge nurse.",
    sections: [
      {
        title: "Clinic Operations",
        questions: [
          "Manages chair turnover and patient throughput",
          "Oversees water-treatment system and AAMI compliance",
          "Coordinates physician orders and lab review",
          "Handles patient grievances and disenrollment",
          "Coordinates transplant referrals",
          "Supports CMS ESRD network reporting",
        ],
      },
      {
        title: "Quality & Compliance",
        questions: [
          "Leads CMS conditions-for-coverage survey readiness",
          "Tracks vascular-access metrics and infections",
          "Manages QAPI projects and PIPs",
          "Reviews adequacy and anemia outcomes",
          "Manages patient and staff safety incidents",
          "Coordinates emergency preparedness",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "emergency-department-er",
    title: "Emergency Department / ER",
    category: "Acute Care",
    summary: "Adult / pediatric emergency department nursing.",
    sections: [
      {
        title: "Triage & Resuscitation",
        questions: [
          "ESI 5-level triage and rapid-assessment protocols",
          "Trauma primary / secondary survey and MCI readiness",
          "Stroke code (tPA / TNK) and STEMI alert",
          "Sepsis screening and bundle compliance",
          "Pediatric emergency assessment and resuscitation",
          "Psychiatric emergency and safety holds",
        ],
      },
      {
        title: "Procedural & Acute Skills",
        questions: [
          "Procedural sedation monitoring and rescue airway",
          "IO access, central line insertion assist, RSI assist",
          "Splinting, wound closure, and lac repair assist",
          "Foley insertion and gastric-tube placement",
          "Lumbar-puncture and paracentesis assist",
          "Conscious sedation per ASA standards",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "endoscopy",
    title: "Endoscopy",
    category: "Procedural",
    summary: "GI endoscopy nursing — pre, intra, post.",
    sections: [
      {
        title: "Endoscopy Procedures",
        questions: [
          "EGD, colonoscopy, ERCP, and EUS room setup",
          "Procedural sedation monitoring (moderate / MAC)",
          "Rescue airway readiness and reversal agents",
          "Specimen handling and pathology routing",
          "Polypectomy, banding, and stent assist",
          "Bronchoscopy assist and high-level disinfection",
        ],
      },
      {
        title: "Scope Reprocessing",
        questions: [
          "Manual cleaning and leak testing per SGNA",
          "Automated endoscope reprocessor (AER) operation",
          "Sterile storage and traceability",
          "Quality monitoring and ATP testing",
          "Vendor maintenance coordination",
          "Documentation per AAMI / Joint Commission",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "healthcare-leadership-competencies",
    title: "Healthcare Leadership Competencies",
    category: "Leadership",
    summary: "Director / Manager / VP-level healthcare leadership.",
    sections: [
      {
        title: "Strategic & Financial",
        questions: [
          "Develops and manages departmental budget",
          "Reads and acts on monthly P&L statements",
          "Builds capital and operating cost-justifications",
          "Negotiates vendor and consulting contracts",
          "Designs strategic planning and growth roadmaps",
          "Evaluates ROI on technology and capital investments",
        ],
      },
      {
        title: "People & Culture",
        questions: [
          "Coaches managers and emerging leaders",
          "Leads engagement strategy and pulse surveys",
          "Manages succession-planning and high-potential pipeline",
          "Resolves complex employee-relations matters",
          "Designs and rolls out culture initiatives",
          "Partners with HR on union, contract, and labor matters",
        ],
      },
      {
        title: "Quality & Safety",
        questions: [
          "Leads quality strategy and patient-safety governance",
          "Sponsors high-reliability organization initiatives",
          "Reviews regulatory readiness across departments",
          "Facilitates ethics and end-of-life conversations",
          "Drives equity, diversity, and inclusion strategy",
          "Manages cross-functional crisis response",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "home-health",
    title: "Home Health",
    category: "Home & Community",
    summary: "Intermittent skilled home-health nursing.",
    sections: [
      {
        title: "OASIS & Documentation",
        questions: [
          "Performs comprehensive OASIS assessment with accuracy",
          "Owns 485 plan-of-care, recertification, and resumption-of-care",
          "Charts within Medicare timeliness and coding requirements",
          "Manages PDGM episode tracking",
          "Manages prior-auth and medical-necessity rationale",
          "Communicates with MD, therapy, and aide team",
        ],
      },
      {
        title: "Skilled Visits",
        questions: [
          "Wound-care visits including negative-pressure and complex dressings",
          "PICC / central-line maintenance and home IV antibiotics",
          "Med reconciliation and teach-back education",
          "Diabetic, CHF, COPD chronic-disease management",
          "Telehealth and remote patient monitoring",
          "Palliative and end-of-life home care",
        ],
      },
      {
        title: "Safety in the Home",
        questions: [
          "Home-safety, fall-risk, and environmental assessment",
          "Caregiver education and burnout monitoring",
          "Emergency-preparedness and disaster planning",
          "Recognition of abuse / neglect / self-harm",
          "Personal safety and de-escalation strategies",
          "Driving and visit-routing efficiency",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "hospice",
    title: "Hospice",
    category: "Palliative",
    summary: "Hospice and end-of-life nursing.",
    sections: [
      {
        title: "Symptom Management",
        questions: [
          "Pain assessment and titration of opioids and adjuvants",
          "Dyspnea, delirium, agitation, and nausea management",
          "Comfort kit utilization and PRN protocol",
          "Subcutaneous medication administration",
          "Wound and skin-integrity comfort measures",
          "Imminence-of-death recognition and family preparation",
        ],
      },
      {
        title: "Care Setting & Coordination",
        questions: [
          "Home, GIP, residential, and inpatient hospice settings",
          "Coordination with chaplain, social work, and bereavement",
          "Medicare hospice benefit, levels-of-care, and recertification",
          "Patient and family education on disease trajectory",
          "Cultural and spiritual responsiveness",
          "Bereavement follow-up and grief support",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "icu",
    title: "ICU",
    category: "Critical Care",
    summary: "Adult intensive-care nursing for unstable / complex patients.",
    sections: [
      {
        title: "Hemodynamic Monitoring",
        questions: [
          "Arterial lines, CVP, Swan-Ganz interpretation and management",
          "Vasoactive drips — norepinephrine, epinephrine, vasopressin, dobutamine",
          "Cardiac output / SVO2 monitoring",
          "Sepsis bundle execution",
          "Recognition and management of shock states",
          "Targeted-temperature management post arrest",
        ],
      },
      {
        title: "Ventilator & Renal",
        questions: [
          "Ventilator management and ARDS protocols",
          "Daily SBT / SAT and ABCDEF bundle",
          "CRRT / dialysis nursing",
          "Tracheostomy care and weaning",
          "Continuous EEG monitoring",
          "ICP and EVD management",
        ],
      },
      {
        title: "Specialty Skills",
        questions: [
          "Post-cardiac surgery management (CABG, valve, ECMO bridging)",
          "IABP, Impella, and ECMO bedside management",
          "Therapeutic plasma exchange",
          "Massive transfusion protocol",
          "End-of-life / palliative-care transitions",
          "Family meetings and goals-of-care coordination",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "infection-control",
    title: "Infection Control",
    category: "Quality",
    summary: "Infection-prevention RN / coordinator role.",
    sections: [
      {
        title: "Surveillance & Reporting",
        questions: [
          "NHSN reporting for HAI metrics (CLABSI, CAUTI, SSI, VAP)",
          "Maintains line-list surveillance for outbreaks",
          "Reports to local / state health departments",
          "Conducts antibiogram review and stewardship support",
          "Tracks employee-health exposures",
          "Maintains TB program and respirator fit testing",
        ],
      },
      {
        title: "Environment of Care",
        questions: [
          "Performs hand-hygiene audits and feedback",
          "Reviews construction risk assessments (ICRA)",
          "Audits sterilization and high-level disinfection compliance",
          "Manages outbreak investigations and contact tracing",
          "Oversees PPE supply and crisis-standards of care",
          "Coordinates with environmental services on terminal cleaning",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "intermediate-care",
    title: "Intermediate Care",
    category: "Acute Care",
    summary: "Intermediate / progressive-care nursing between ICU and Med-Surg.",
    sections: [
      {
        title: "Cardiopulmonary Care",
        questions: [
          "High-flow nasal cannula and non-invasive ventilation (BiPAP / CPAP)",
          "Vasoactive drips within unit-defined scope",
          "Continuous cardiac monitoring and arrhythmia recognition",
          "Post-op cardiac, vascular, and step-down chest-tube management",
          "Heart-failure titration",
          "Sepsis re-escalation triggers and early-warning scores",
        ],
      },
      {
        title: "Specialty Care",
        questions: [
          "Neuro step-down — post-craniotomy, stroke monitoring",
          "Post-op craniotomy / spine care",
          "End-of-life and palliative transitions",
          "Acute oncology and tumor-lysis syndrome",
          "Renal step-down and acute kidney injury management",
          "GI bleed and liver failure management",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "interventional-radiology",
    title: "Interventional Radiology",
    category: "Procedural",
    summary: "IR nursing — procedural support, sedation, recovery.",
    sections: [
      {
        title: "IR Procedures",
        questions: [
          "Vascular-access procedures (port, PICC, dialysis catheter)",
          "Embolization (uterine, tumor, trauma, AVM)",
          "Biliary, GU, and abdominal drainage placement",
          "Nephrostomy, gastrostomy, and J-tube placement",
          "Tumor ablation (RFA, microwave, cryo)",
          "TIPS and venous interventions",
        ],
      },
      {
        title: "Sedation & Safety",
        questions: [
          "Moderate sedation monitoring per ASA",
          "Capnography and supplemental oxygen titration",
          "Reversal agents and rescue airway",
          "Contrast reaction recognition and treatment",
          "Radiation safety and dose tracking",
          "Post-procedure recovery and discharge",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "labor-and-delivery",
    title: "Labor and Delivery",
    category: "Maternal-Child",
    summary: "L&D nursing across antepartum, intrapartum, and postpartum.",
    sections: [
      {
        title: "Intrapartum",
        questions: [
          "EFM interpretation per NICHD categories I/II/III",
          "Induction / augmentation with oxytocin, misoprostol, Foley",
          "Epidural placement assistance and post-epidural monitoring",
          "Vaginal-delivery support, shoulder dystocia, postpartum hemorrhage",
          "Cesarean section circulating / scrub role",
          "Operative vaginal delivery — vacuum / forceps assist",
        ],
      },
      {
        title: "High-Risk Obstetrics",
        questions: [
          "Preeclampsia, HELLP, and magnesium sulfate protocols",
          "Antepartum bleeding (placenta previa, abruption)",
          "Diabetes management in pregnancy",
          "Multiple gestation and preterm labor management",
          "Maternal cardiac and hypertensive emergencies",
          "PPH bundle and transfusion management",
        ],
      },
      {
        title: "Newborn Stabilization",
        questions: [
          "NRP and golden-hour resuscitation",
          "Apgar scoring and newborn assessment",
          "Skin-to-skin / breastfeeding initiation",
          "Late-preterm and special-care nursery transition",
          "Hypoglycemia and respiratory-distress recognition",
          "Discharge teaching and car-seat education",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "long-term-care",
    title: "Long Term Care",
    category: "Post-Acute",
    summary: "SNF / LTC RN — chronic complex residents.",
    sections: [
      {
        title: "Resident Care",
        questions: [
          "MDS 3.0 data collection and PDPM accuracy",
          "Pressure-injury prevention and Braden scoring",
          "IV antibiotics, trach / vent care, G-tube management",
          "Dementia / behavioral care and fall-prevention",
          "Medication-pass efficiency and high-risk med reconciliation",
          "Care-conference leadership and family communication",
        ],
      },
      {
        title: "Regulatory & Quality",
        questions: [
          "F-tag knowledge and survey readiness",
          "QAPI and PIP project participation",
          "Self-reportable event escalation",
          "Restraint reduction and behavioral-health programs",
          "Five-Star quality measure tracking",
          "Antibiotic stewardship and pharmacy partnership",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "med-surg",
    title: "Med-Surg",
    category: "Acute Care",
    summary: "Adult medical-surgical inpatient nursing.",
    sections: [
      {
        title: "Core Med-Surg Skills",
        questions: [
          "IV initiation, PICC line care, central-line dressing changes",
          "Blood-product administration and transfusion-reaction management",
          "Wound care, ostomy care, pressure-injury staging",
          "Pain management including PCA, epidural, opioid reconciliation",
          "Patient education on diabetes, CHF, COPD, post-op discharge",
          "Rapid response / code response as primary nurse",
        ],
      },
      {
        title: "Specialty Case Mix",
        questions: [
          "Telemetry / arrhythmia recognition",
          "Pre-op / post-op orthopedic, vascular, GI care",
          "Acute neuro (stroke, post-craniotomy) inpatient management",
          "Acute oncology and chemotherapy support",
          "Renal-failure and dialysis-day coordination",
          "Bariatric and palliative-care transitions",
        ],
      },
      INFECTION_CONTROL,
      PATIENT_SAFETY,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "neonatal-icu",
    title: "Neonatal ICU",
    category: "Maternal-Child",
    summary: "Level II–IV NICU nursing.",
    sections: [
      {
        title: "Neonatal Resuscitation & Stabilization",
        questions: [
          "Golden-hour resuscitation, NRP, and umbilical line management",
          "Conventional / HFOV / iNO ventilation",
          "Surfactant administration and INSURE / LISA",
          "Therapeutic hypothermia for HIE",
          "Hypoglycemia and IV-fluid management",
          "Sepsis evaluation and antibiotic stewardship",
        ],
      },
      {
        title: "Growth & Family Care",
        questions: [
          "Enteral feeding progression and TPN",
          "NEC recognition and management",
          "Developmental and skin-to-skin / kangaroo care",
          "Family integration and parent education",
          "Discharge planning — car seat, home apnea monitor",
          "Bereavement and loss support",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "nurse-practitioner",
    title: "Nurse Practitioner",
    category: "Advanced Practice",
    summary: "NP across acute, primary, urgent, and specialty care.",
    sections: [
      {
        title: "Diagnostic Reasoning",
        questions: [
          "Comprehensive history and physical examination",
          "Differential diagnosis and evidence-based work-up",
          "Acute and chronic disease management",
          "Pharmacotherapy and prescriptive authority",
          "Procedural skills within state scope (lac repair, biopsy, joint injection)",
          "Specialty consult coordination",
        ],
      },
      {
        title: "Patient Management",
        questions: [
          "Health promotion, screening, and counseling",
          "Mental-health and behavioral integration",
          "Care-transition and admission / discharge documentation",
          "Telemedicine and asynchronous patient communication",
          "Quality-measure adherence (HEDIS, MIPS)",
          "Patient education and shared decision-making",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "ob-labor-and-delivery",
    title: "OB / Labor and Delivery",
    category: "Maternal-Child",
    summary: "OB/L&D — labor, delivery, postpartum, and newborn.",
    sections: [
      {
        title: "Labor & Delivery",
        questions: [
          "EFM interpretation NICHD I/II/III",
          "Cervical exam and Bishop scoring",
          "Induction / augmentation management",
          "Epidural placement support and post-epidural care",
          "Vaginal and cesarean delivery support",
          "Postpartum hemorrhage and shoulder dystocia drills",
        ],
      },
      {
        title: "Postpartum & Newborn",
        questions: [
          "Postpartum recovery, fundal checks, lochia assessment",
          "Newborn assessment, NRP, and golden-hour care",
          "Lactation initiation and breastfeeding support",
          "Newborn metabolic / hearing screening",
          "Discharge teaching and family education",
          "Mood-disorder screening and referrals",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "oncology",
    title: "Oncology",
    category: "Specialty",
    summary: "Inpatient and outpatient oncology nursing.",
    sections: [
      {
        title: "Chemotherapy & Biotherapy",
        questions: [
          "Chemotherapy administration per ONS guidelines",
          "Hazardous-drug PPE and USP 800 compliance",
          "Extravasation prevention and management",
          "Hypersensitivity-reaction management",
          "Immunotherapy / CAR-T administration support",
          "Targeted therapy and oral-chemo education",
        ],
      },
      {
        title: "Symptom & Palliative Care",
        questions: [
          "Pain, nausea, mucositis, and fatigue management",
          "Tumor-lysis syndrome recognition",
          "Neutropenia, thrombocytopenia, and anemia management",
          "End-of-life and palliative-care transitions",
          "Psychosocial support and counseling referrals",
          "Survivorship education and follow-up",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "operating-room",
    title: "Operating Room",
    category: "Surgical",
    summary: "Perioperative circulating / scrub nursing.",
    sections: [
      {
        title: "Circulating Role",
        questions: [
          "Pre-procedure verification and time-out per AORN",
          "Sterile-field setup, draping, and positioning",
          "Specimen handling and chain-of-custody",
          "Counts — instrument, sponge, sharp",
          "Vendor-credentialing and loaner-tray management",
          "Handoff to PACU per AORN",
        ],
      },
      {
        title: "Scrub Role",
        questions: [
          "Scrub experience in general, ortho, vascular, neuro, cardiac, robotic",
          "Laser safety, electrosurgery, and tourniquet management",
          "Sterile-instrument handling and microscope draping",
          "Power-equipment handling (drill, saw, reamer)",
          "Case-cart preparation and instrument identification",
          "Emergency conversion and crisis management",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "or-surgical",
    title: "OR / Surgical",
    category: "Surgical",
    summary: "Surgical RN — broader perioperative competency.",
    sections: [
      {
        title: "Perioperative Workflow",
        questions: [
          "Pre-op assessment and consent verification",
          "Intra-op circulating and scrub responsibilities",
          "Anesthesia partnership — induction, emergence, MH readiness",
          "Specimen handling and pathology routing",
          "Documentation and AORN-aligned charting",
          "PACU handoff and post-op communication",
        ],
      },
      {
        title: "Specialty Lines",
        questions: [
          "General, ortho, GYN, urology, ENT, plastic, vascular cases",
          "Robotic (da Vinci) docking and assist",
          "Trauma and emergency cases",
          "Pediatric perioperative considerations",
          "Bariatric positioning and equipment",
          "Cardiac / thoracic case familiarity",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "pacu",
    title: "PACU",
    category: "Surgical",
    summary: "Phase I & II post-anesthesia recovery.",
    sections: [
      {
        title: "Recovery Phase I",
        questions: [
          "Aldrete scoring and Phase I discharge criteria",
          "Airway management post-extubation and rescue airway",
          "PONV protocols and opioid-sparing analgesia",
          "Regional-block monitoring and reassessment",
          "Pediatric PACU and emergence-delirium management",
          "Unanticipated escalation to ICU",
        ],
      },
      {
        title: "Recovery Phase II",
        questions: [
          "Phase II discharge criteria and education",
          "Outpatient surgical recovery and home-going criteria",
          "PCA / regional pump education",
          "Post-op nausea and pain plan",
          "Caregiver and discharge education",
          "Documentation and handoff",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "pct",
    title: "PCT",
    category: "Support",
    summary: "Patient Care Technician scope.",
    sections: [
      {
        title: "Daily Care",
        questions: [
          "ADL support — bathing, toileting, feeding, ambulation",
          "Vitals, I/O measurement, and POC glucose",
          "Safe transfers, mechanical-lift use, fall-prevention",
          "Foley care, bed-scale weights, specimen collection",
          "12-lead EKG acquisition and telemetry placement",
          "Restraint / safety-attendant documentation",
        ],
      },
      INFECTION_CONTROL,
      PATIENT_SAFETY,
    ],
  },
  {
    slug: "patient-care-technician",
    title: "Patient Care Technician",
    category: "Support",
    summary: "Acute-hospital PCT competencies.",
    sections: [
      {
        title: "Acute-Care PCT",
        questions: [
          "ADL support and 1:1 sitter responsibilities",
          "Vitals, weights, I/O documentation",
          "EKG, telemetry leads, POC glucose",
          "Foley care, specimen collection",
          "Phlebotomy support",
          "Rapid escalation to RN for changes in condition",
        ],
      },
      INFECTION_CONTROL,
      PATIENT_SAFETY,
    ],
  },
  {
    slug: "pediatric-emergency-department",
    title: "Pediatric Emergency Department",
    category: "Maternal-Child",
    summary: "Pediatric ED nursing.",
    sections: [
      {
        title: "Pediatric Triage & Resuscitation",
        questions: [
          "Pediatric ESI triage and red-flag recognition",
          "PALS resuscitation and rapid response",
          "Pediatric pharmacology and weight-based dosing",
          "Procedural sedation and family-centered care",
          "Trauma activation and child-abuse recognition",
          "Mental-health screening and safety holds",
        ],
      },
      {
        title: "Common Pediatric Conditions",
        questions: [
          "Respiratory distress (asthma, bronchiolitis, croup)",
          "Dehydration and IV/PO rehydration",
          "Sepsis recognition and bundle initiation",
          "Seizure and status-epilepticus management",
          "Foreign body, ingestion, and toxicology",
          "Bone / joint injury and orthopedic care",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "pediatrics",
    title: "Pediatrics",
    category: "Maternal-Child",
    summary: "Inpatient pediatrics — acute and intermediate.",
    sections: [
      {
        title: "Pediatric Acute",
        questions: [
          "Age-appropriate assessment and pain scoring",
          "Pediatric medication dosing and IV access",
          "Family-centered care and child-life integration",
          "Common diagnoses — asthma, gastroenteritis, RSV, sepsis",
          "Pediatric oncology / hematology basics",
          "Discharge teaching and follow-up coordination",
        ],
      },
      {
        title: "Specialty Considerations",
        questions: [
          "Neuro / seizure monitoring",
          "Post-op pediatric surgical management",
          "Cardiac / cystic fibrosis pathways",
          "Behavioral / mental-health support",
          "Adolescent privacy and consent",
          "Child abuse / neglect reporting",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "peri-operative",
    title: "Peri-Operative",
    category: "Surgical",
    summary: "Peri-op RN — pre-op, intra-op, post-op.",
    sections: [
      {
        title: "Pre-op",
        questions: [
          "H&P review and consent verification",
          "Skin antisepsis and pre-op bathing",
          "VTE / SSI prophylaxis",
          "Antibiotic timing and dosing",
          "Patient and family education",
          "Coordination with anesthesia",
        ],
      },
      {
        title: "Intra-op / Post-op",
        questions: [
          "Circulating and scrub experience",
          "Specimen and count accuracy",
          "Hand-off to PACU",
          "Phase I and Phase II PACU recovery",
          "Pain and PONV management",
          "Discharge readiness and education",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "picu",
    title: "PICU",
    category: "Critical Care",
    summary: "Pediatric ICU — medical, surgical, cardiac.",
    sections: [
      {
        title: "Pediatric Critical Care",
        questions: [
          "Pediatric vasoactive titration and fluid resuscitation",
          "Pediatric mechanical ventilation and extubation readiness",
          "Post-op cardiac (single-ventricle physiology, pacing)",
          "Status epilepticus and continuous EEG management",
          "PALS / ENPC current and applied in code events",
          "Family-centered rounding and end-of-life discussions",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "post-partum",
    title: "Post Partum",
    category: "Maternal-Child",
    summary: "Postpartum / Mother-Baby unit nursing.",
    sections: [
      {
        title: "Maternal Care",
        questions: [
          "Postpartum hemorrhage recognition and management",
          "Fundal, lochia, and perineal assessment",
          "Pain management — including post-cesarean",
          "Hypertensive disorders postpartum",
          "Breastfeeding initiation and latch support",
          "Mood-disorder screening and referrals",
        ],
      },
      {
        title: "Newborn Care",
        questions: [
          "Newborn assessment and routine care",
          "Newborn metabolic, CCHD, and hearing screens",
          "Hypoglycemia / hyperbilirubinemia management",
          "Lactation support and formula education",
          "Discharge teaching and car-seat education",
          "Safe-sleep practices",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "psychiatric-tech",
    title: "Psychiatric Tech",
    category: "Behavioral Health",
    summary: "Psych tech / mental-health technician scope.",
    sections: [
      {
        title: "Behavioral Care",
        questions: [
          "Therapeutic communication and de-escalation",
          "Suicide-risk monitoring and 1:1 observation",
          "Group facilitation and milieu management",
          "ADL and self-care support",
          "Restraint / seclusion documentation per scope",
          "Crisis-prevention intervention (CPI / NCI)",
        ],
      },
      INFECTION_CONTROL,
      PATIENT_SAFETY,
    ],
  },
  {
    slug: "psychiatry",
    title: "Psychiatry",
    category: "Behavioral Health",
    summary: "Inpatient and outpatient psychiatric nursing.",
    sections: [
      {
        title: "Psychiatric Nursing",
        questions: [
          "Mental-status exam and risk assessment",
          "Suicide / homicide / elopement risk and precautions",
          "Crisis intervention and de-escalation",
          "Psychotropic medication administration and monitoring",
          "ECT support and pre/post recovery",
          "Substance-use co-occurring disorder management",
        ],
      },
      {
        title: "Therapeutic Milieu",
        questions: [
          "Group and milieu therapy participation",
          "Trauma-informed care",
          "Restraint / seclusion management per Joint Commission",
          "Mandated reporting and safety planning",
          "Family education and discharge planning",
          "Telepsychiatry workflow",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "registered-nurse-checklist",
    title: "Registered Nurse Checklist",
    category: "Generalist",
    summary: "Generalist RN baseline competency across acute care.",
    sections: [
      {
        title: "Core Clinical Skills",
        questions: [
          "Comprehensive head-to-toe assessment",
          "IV initiation, central-line care, and PICC management",
          "Medication administration including high-alert drugs",
          "Wound care and skin-integrity management",
          "Pain management and patient education",
          "Rapid response and code response",
        ],
      },
      INFECTION_CONTROL,
      PATIENT_SAFETY,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "registered-nurse-appeals-writer",
    title: "Registered Nurse Appeals Writer",
    category: "Care Coordination",
    summary: "RN appeals writer — payer denials and clinical narratives.",
    sections: [
      {
        title: "Clinical Review",
        questions: [
          "InterQual / MCG criteria interpretation",
          "Chart review and clinical-evidence extraction",
          "Drafting medical-necessity narratives",
          "Peer-to-peer preparation and clinical summary",
          "Writes overturn and partial-overturn appeals",
          "Tracks appeal outcomes and trend reporting",
        ],
      },
      {
        title: "Regulatory & Process",
        questions: [
          "Knows commercial / Medicare / Medicaid appeals timelines",
          "Maintains HIPAA and PHI handling standards",
          "Coordinates with physicians and case management",
          "Maintains denial-prevention workflows",
          "Interprets contract language and benefits",
          "Reports KPIs to leadership",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "registered-nurse-endoscopy",
    title: "Registered Nurse Endoscopy",
    category: "Procedural",
    summary: "Endoscopy RN scope.",
    sections: [
      {
        title: "Endoscopy",
        questions: [
          "EGD / colonoscopy / ERCP / EUS support",
          "Procedural sedation monitoring",
          "Polypectomy, banding, and stent assist",
          "Reversal-agent administration",
          "Specimen routing",
          "Pre/post-procedure assessment",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "rn-occupational-employee-health",
    title: "RN Occupational and Employee Health",
    category: "Occupational",
    summary: "Occupational and employee-health nursing.",
    sections: [
      {
        title: "Employee Health",
        questions: [
          "Pre-employment screening and immunization review",
          "Annual TB screening and respirator fit testing",
          "Bloodborne-pathogen exposure response",
          "Workers' compensation case management",
          "Return-to-work and accommodation evaluations",
          "OSHA injury / illness reporting",
        ],
      },
      {
        title: "Occupational Programs",
        questions: [
          "Hearing-conservation and audiometry program",
          "Ergonomic assessments and injury prevention",
          "Drug / alcohol screening (DOT and non-DOT)",
          "Travel-medicine and immunization counseling",
          "Onsite emergency response and AED program",
          "Wellness and health-promotion programs",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "registered-nurse-supervisor",
    title: "Registered Nurse Supervisor",
    category: "Leadership",
    summary: "House supervisor / nursing supervisor.",
    sections: [
      {
        title: "House Supervision",
        questions: [
          "Manages real-time bed assignment and capacity",
          "Coordinates rapid-response and code-blue",
          "Resolves staffing escalations and float deployment",
          "Manages mass-casualty / disaster activation",
          "Oversees AMA, elopement, and security events",
          "Coordinates inter-facility transfers",
        ],
      },
      {
        title: "Leadership",
        questions: [
          "Mentors charge nurses and unit leaders",
          "Conducts clinical decision-making support",
          "Reports to senior leadership on shift events",
          "Maintains regulatory and Joint Commission readiness",
          "Coordinates with administrator-on-call",
          "Investigates patient and family complaints",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "skilled-nursing",
    title: "Skilled Nursing",
    category: "Post-Acute",
    summary: "Skilled-nursing facility / sub-acute RN.",
    sections: [
      {
        title: "Sub-Acute Care",
        questions: [
          "Post-hospital sub-acute admissions and stabilization",
          "IV antibiotic, wound, and ostomy management",
          "Trach, vent, and high-flow O2 management",
          "PDPM documentation and MDS partnership",
          "Care planning and family conferences",
          "Discharge to home / community coordination",
        ],
      },
      INFECTION_CONTROL,
      PATIENT_SAFETY,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "telemetry",
    title: "Telemetry",
    category: "Acute Care",
    summary: "Continuous cardiac monitoring nursing.",
    sections: [
      {
        title: "Cardiac Monitoring",
        questions: [
          "Rhythm interpretation — AFib, VT, heart blocks, pacing",
          "IV antiarrhythmic administration (amiodarone, diltiazem)",
          "Post-PCI recovery and sheath management",
          "Heart-failure titration",
          "Pacemaker / ICD / LVAD basics",
          "Event recognition and rapid escalation",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "telemetry-stepdown",
    title: "Telemetry / Stepdown",
    category: "Acute Care",
    summary: "Telemetry and stepdown combined competency.",
    sections: [
      {
        title: "Telemetry",
        questions: [
          "Continuous arrhythmia recognition and escalation",
          "Cardiac drips per unit-defined scope",
          "Heart-failure titration",
          "Post-PCI recovery and groin / radial site care",
          "Device education — pacemaker, ICD, LVAD basics",
          "BiPAP / CPAP titration",
        ],
      },
      {
        title: "Stepdown",
        questions: [
          "High-flow nasal cannula and NIV",
          "Vasoactive titration within scope",
          "Post-op CABG / vascular stepdown",
          "Neuro stepdown post-craniotomy",
          "Sepsis re-escalation triggers",
          "Palliative-care transitions",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "travel-nursing",
    title: "Travel Nursing",
    category: "Generalist",
    summary: "Travel-RN baseline — fast-onboarding clinician.",
    sections: [
      {
        title: "Adaptability & Onboarding",
        questions: [
          "Self-directed unit onboarding under tight timelines",
          "Quickly learns new EMR systems",
          "Adapts to facility-specific protocols and order sets",
          "Performs hand-offs, charting, and documentation per host facility",
          "Engages constructively in interdisciplinary teams",
          "Maintains compliance binders, licenses, and credentials",
        ],
      },
      {
        title: "Clinical Generalist Skills",
        questions: [
          "Med-surg and telemetry baseline",
          "ICU or ED baseline (per specialty)",
          "Procedural and IV skills",
          "Patient education across cultures",
          "Code response and rapid-response participation",
          "Self-care and burnout prevention",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "urgent-care",
    title: "Urgent Care",
    category: "Outpatient",
    summary: "Urgent-care RN scope.",
    sections: [
      {
        title: "Urgent Care",
        questions: [
          "Triage and chief-complaint capture",
          "Suturing assist, splinting, lac repair",
          "Procedural skills — I&D, foreign-body removal",
          "POC testing — flu, strep, COVID, mono, glucose",
          "X-ray order coordination with rad-tech",
          "Discharge / referral coordination",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "wound-care",
    title: "Wound Care",
    category: "Specialty",
    summary: "Wound-care nurse / WOC nurse scope.",
    sections: [
      {
        title: "Wound Assessment & Management",
        questions: [
          "Pressure-injury staging and Braden scoring",
          "Diabetic foot ulcer, venous, and arterial wound management",
          "Negative-pressure wound therapy (NPWT)",
          "Debridement (sharp, enzymatic, autolytic) within scope",
          "Compression-therapy application",
          "Tracheostomy, ostomy, and fistula management",
        ],
      },
      {
        title: "Program & Education",
        questions: [
          "Wound-prevention bundle implementation",
          "Hospital-acquired pressure-injury reduction",
          "Patient and caregiver wound education",
          "Telehealth wound surveillance",
          "Coordination with vascular and surgical specialists",
          "WOCN / CWCN certification maintenance",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
];

/* ============================================================
 *  THERAPIST CHECKLISTS
 * ============================================================ */
const therapist = [
  {
    slug: "acute-rehab",
    title: "Acute Rehab",
    category: "Inpatient Rehab",
    summary: "Acute / inpatient rehabilitation therapy across stroke, SCI, TBI, ortho.",
    sections: [
      {
        title: "Inpatient Rehab Practice",
        questions: [
          "Stroke rehabilitation — motor relearning, constraint-induced",
          "Spinal-cord-injury rehab and wheelchair prescription",
          "TBI cognitive and physical rehabilitation",
          "Amputee rehab — pre- and post-prosthetic",
          "Functional Independence Measure (FIM) / GG-codes scoring",
          "3-hour rule compliance and team conferences",
        ],
      },
      {
        title: "Discharge & Outcomes",
        questions: [
          "Discharge planning to home, SNF, or LTACH",
          "Caregiver training and home-program design",
          "Equipment recommendations (DME, AFOs, splints)",
          "Outpatient and home-health handoff",
          "Outcome measurement and goal writing",
          "Family conferences and counseling",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "occupational-therapy-skills-checklist",
    title: "Occupational Therapy Skills Checklist",
    category: "OT",
    summary: "Comprehensive OT competency across acute, rehab, outpatient, peds.",
    sections: [
      {
        title: "Evaluation & Treatment",
        questions: [
          "ADL / IADL evaluation and treatment planning",
          "Cognitive assessment (MoCA, Allen, A&O)",
          "Upper-extremity assessment and treatment",
          "Sensory integration techniques",
          "Adaptive-equipment recommendations",
          "Splinting and orthotic fabrication (when in scope)",
        ],
      },
      {
        title: "Setting-Specific Skills",
        questions: [
          "Acute / ICU early mobility and ADL retraining",
          "Inpatient rehab post-stroke / SCI / TBI",
          "Outpatient hand therapy",
          "Pediatric / school-based interventions",
          "SNF / home-health productivity standards",
          "Mental / behavioral-health programming",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "physical-therapist-self-assessment",
    title: "Physical Therapist Self Assessment",
    category: "PT",
    summary: "Physical therapist self-assessment across settings.",
    sections: [
      {
        title: "Examination & Intervention",
        questions: [
          "Comprehensive musculoskeletal and neurologic exam",
          "Manual therapy — mobilization, manipulation, soft tissue",
          "Therapeutic exercise prescription and progression",
          "Modalities — TENS, ultrasound, ice, heat",
          "Outcome measures (PSFS, LEFS, DASH, TUG, 6MWT)",
          "Patient education and home-program design",
        ],
      },
      {
        title: "Specialty Settings",
        questions: [
          "Acute care and ICU early mobility",
          "Inpatient rehab and SCI / stroke",
          "Outpatient ortho / sports / post-op",
          "Pediatric / early intervention",
          "Home health and OASIS competency",
          "SNF / LTC and PDPM",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "pt-pta-skills-checklist",
    title: "PT / PTA Skills Checklist",
    category: "PT",
    summary: "Combined PT / PTA skills checklist.",
    sections: [
      {
        title: "Therapeutic Exercise & Manual",
        questions: [
          "Therapeutic exercise progression",
          "Manual therapy techniques",
          "Gait training and assistive-device selection",
          "Balance and vestibular training",
          "Wound-care basics (where in scope)",
          "Modalities — heat, ice, e-stim, ultrasound",
        ],
      },
      {
        title: "Documentation & Coordination",
        questions: [
          "Skilled documentation and CPT coding",
          "Plan-of-care collaboration with PT",
          "Outcome measurement and re-eval",
          "Patient education and home-exercise programs",
          "Productivity and time management",
          "Caregiver training",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "respiratory-therapist",
    title: "Respiratory Therapist",
    category: "RT",
    summary: "RT competency across ICU, NICU, ED, and floor.",
    sections: [
      {
        title: "Mechanical Ventilation",
        questions: [
          "Invasive ventilation modes — AC, SIMV, PSV, APRV",
          "ARDS protocols including prone and low-Vt",
          "NIV (BiPAP / CPAP) and HFNC",
          "ABG interpretation and ventilator weaning",
          "Bronchoscopy assist and difficult airway readiness",
          "ECMO awareness and ventilator coordination",
        ],
      },
      {
        title: "Specialty Populations",
        questions: [
          "Neonatal resuscitation (NRP) and HFOV / iNO",
          "Pediatric airway management",
          "ED code-response and RSI assist",
          "Floor / med-surg respiratory therapy",
          "Pulmonary-rehab program delivery",
          "Home / outpatient pulmonary clinics",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "speech-language-pathologist",
    title: "Speech Language Pathologist",
    category: "SLP",
    summary: "SLP across adult acute, outpatient, peds, SNF.",
    sections: [
      {
        title: "Evaluation",
        questions: [
          "Bedside swallow evaluation and aspiration screening",
          "MBSS / FEES interpretation",
          "Cognitive-linguistic assessment post-CVA / TBI",
          "Aphasia and apraxia evaluation",
          "Pediatric standardized articulation / phonology testing",
          "Language sampling and analysis",
        ],
      },
      {
        title: "Treatment",
        questions: [
          "Adult dysphagia treatment and diet recommendations",
          "Aphasia treatment — CIAT, SFA, script training",
          "Voice and motor-speech therapy (LSVT, SPEAK OUT)",
          "Pediatric articulation, language, fluency",
          "AAC evaluation and implementation",
          "Cognitive-communication for dementia",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
];

/* ============================================================
 *  OTHER (ALLIED) CHECKLISTS
 * ============================================================ */
const other = [
  {
    slug: "medical-assistant",
    title: "Medical Assistant",
    category: "Ambulatory",
    summary: "Clinical / Ambulatory MA competency.",
    sections: [
      {
        title: "Clinical Skills",
        questions: [
          "Vitals, height / weight / BMI, pain screening, rooming",
          "Phlebotomy, capillary draws, point-of-care testing",
          "Injections — IM, SubQ, intradermal; vaccines",
          "Procedure assist — EKG, spirometry, suture removal, ear lavage",
          "Medication reconciliation and refill queue",
          "EMR navigation and ICD-10 / CPT basics",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "medical-laboratory",
    title: "Medical Laboratory",
    category: "Laboratory",
    summary: "Medical lab scientist / technician competency.",
    sections: [
      {
        title: "Bench Skills",
        questions: [
          "Chemistry analyzers — operation, QC, troubleshooting",
          "Hematology — CBC, diff, coag analyzer, manual diffs",
          "Microbiology — plating, gram stains, automated ID",
          "Blood bank — ABO/Rh, crossmatch, antibody screens",
          "Urinalysis — automated and manual microscopy",
          "Molecular assays — GeneXpert, BioFire, PCR",
        ],
      },
      {
        title: "Quality & LIS",
        questions: [
          "Daily / weekly / monthly QC and Levey-Jennings review",
          "CAP / CLIA documentation",
          "LIS verification (Beaker, Sunquest, Cerner)",
          "Specimen processing and labeling accuracy",
          "Critical-result reporting",
          "Instrument maintenance",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "cna",
    title: "CNA",
    category: "Support",
    summary: "Certified nursing assistant — acute, LTC, home.",
    sections: [
      {
        title: "Daily Resident Care",
        questions: [
          "ADL support — bathing, toileting, feeding, ambulation, turning",
          "Vital signs and intake / output measurement",
          "Mechanical lifts and safe-transfer techniques",
          "Skin assessment and pressure-injury reporting",
          "Foley care and bed-scale weights",
          "Specimen collection within scope",
        ],
      },
      {
        title: "Resident Rights & Behavior",
        questions: [
          "Resident-centered care and dignity-first practice",
          "Dementia care and behavior de-escalation",
          "Restraint and safety-attendant documentation",
          "End-of-life comfort care",
          "Reportable events and escalation to RN",
          "Family communication and education",
        ],
      },
      INFECTION_CONTROL,
    ],
  },
  {
    slug: "lpn",
    title: "LPN",
    category: "Generalist",
    summary: "Licensed practical nurse — acute, LTC, ambulatory.",
    sections: [
      {
        title: "Core LPN Skills",
        questions: [
          "Med pass — oral, IM, SubQ within state scope",
          "Wound care and dressing changes",
          "IV maintenance / monitoring (per state scope)",
          "Foley insertion / care",
          "Ostomy and trach care",
          "Patient education and discharge teaching",
        ],
      },
      {
        title: "Care Coordination",
        questions: [
          "Charting on EMR and SBAR handoff",
          "Med reconciliation and refill workflow",
          "Triage and chief-complaint capture (clinic)",
          "Vital signs trending and escalation to RN",
          "Family education and counseling",
          "Compliance with state scope of practice",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "phlebotomy",
    title: "Phlebotomy",
    category: "Laboratory",
    summary: "Phlebotomy technician competency.",
    sections: [
      {
        title: "Specimen Collection",
        questions: [
          "Adult, pediatric, and geriatric venipuncture",
          "Capillary / heel-stick collection for neonates",
          "Order-of-draw and specimen labeling accuracy",
          "Blood-culture sterile technique",
          "Difficult-access strategies (oncology, dialysis)",
          "Time-critical specimen handling (tox, coag)",
        ],
      },
      {
        title: "Patient & Workflow",
        questions: [
          "Patient identification per CLSI GP41-A6",
          "Specimen rejection criteria and redraws",
          "LIS order entry basics",
          "POC glucose and finger-stick procedures",
          "Customer service and rounds efficiency",
          "Hazard-communication and biohazard transport",
        ],
      },
      INFECTION_CONTROL,
    ],
  },
  {
    slug: "sterile-processing-technologist",
    title: "Sterile Processing Technologist",
    category: "Surgical Support",
    summary: "Decontamination, assembly, sterilization, and storage.",
    sections: [
      {
        title: "Decontamination & Assembly",
        questions: [
          "Enzymatic pre-clean and ultrasonic cycles",
          "Instrument identification, inspection, and functional testing",
          "Assembly and peel-pouch / wrap / rigid-container packaging per AAMI ST79",
          "Tray composition and instrument-counting accuracy",
          "Loaner-tray chain-of-custody and vendor credentialing",
          "Endoscope reprocessing per SGNA / AAMI",
        ],
      },
      {
        title: "Sterilization & Storage",
        questions: [
          "Steam, low-temp (H₂O₂, EtO), and HLD cycle parameters",
          "Biological / chemical indicator monitoring and load release",
          "Sterile-storage shelf-life and traceability",
          "Recall management and look-back procedures",
          "Quality monitoring and ATP testing",
          "OR turnover and rapid-priority flips",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "social-worker",
    title: "Social Worker",
    category: "Care Coordination",
    summary: "Hospital / outpatient medical social work.",
    sections: [
      {
        title: "Psychosocial & Discharge",
        questions: [
          "Psychosocial assessment and risk stratification",
          "Discharge planning and post-acute placement",
          "Crisis intervention and safety planning",
          "Domestic-violence, abuse, and neglect reporting",
          "Substance-use referrals and counseling",
          "End-of-life and bereavement support",
        ],
      },
      {
        title: "Resources & Advocacy",
        questions: [
          "Insurance and Medicaid / Medicare advocacy",
          "Community resource navigation",
          "Adoption / foster-care coordination (where applicable)",
          "Adult / child protective services partnership",
          "Cultural and linguistic responsiveness",
          "Documentation per LCSW scope",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "surgical-technologist",
    title: "Surgical Technologist",
    category: "Surgical Support",
    summary: "Surgical scrub tech across general, ortho, cardiac, neuro.",
    sections: [
      {
        title: "Scrub Role",
        questions: [
          "Sterile-field setup and draping",
          "Laparoscopic and robotic case assist (da Vinci docking)",
          "Suture, stapler, and energy-device handling",
          "Specimen handling and chain-of-custody",
          "Emergency conversion (lap → open) readiness",
          "Count accuracy and timeout participation",
        ],
      },
      {
        title: "Specialty Lines",
        questions: [
          "General, ortho (joints, spine, trauma)",
          "Cardiac / vascular open and endovascular",
          "Neuro — cranial, spine, functional",
          "GYN, urology, ENT, plastics",
          "Robotic case docking and conversion",
          "Pediatric perioperative considerations",
        ],
      },
      INFECTION_CONTROL,
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "pharmacist",
    title: "Pharmacist",
    category: "Pharmacy",
    summary: "Hospital / retail / specialty pharmacist.",
    sections: [
      {
        title: "Clinical Pharmacy",
        questions: [
          "Order verification and clinical interventions",
          "Antimicrobial stewardship participation",
          "Anticoagulation, TPN, and renal dose adjustment",
          "Chemotherapy verification (USP 800)",
          "Discharge counseling and medication reconciliation",
          "Drug-information consults",
        ],
      },
      {
        title: "Operations",
        questions: [
          "USP 797 / 800 compliance",
          "Controlled-substance accountability and DEA",
          "Pharmacy automation (Pyxis / Omnicell / Carousel)",
          "Inventory and 340B program management",
          "P&T committee participation",
          "Tech supervision and check-off",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "pharmacy-technician",
    title: "Pharmacy Technician",
    category: "Pharmacy",
    summary: "Inpatient / retail pharmacy technician.",
    sections: [
      {
        title: "Compounding & Dispensing",
        questions: [
          "USP 797 aseptic compounding",
          "USP 800 hazardous-drug handling",
          "Sterile / non-sterile compounding (USP 795)",
          "Automated dispensing cabinet (Pyxis / Omnicell) management",
          "Code-cart restocking and crash-tray seal verification",
          "Repackaging and unit-dose preparation",
        ],
      },
      {
        title: "Workflow & Compliance",
        questions: [
          "Rx intake, data entry, insurance adjudication",
          "Specialty PA workflow and copay assistance",
          "Inventory management and DEA C-II",
          "Immunization support and vaccine record",
          "POS operations and reconciliation",
          "Customer service and triage to pharmacist",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "ct-technologist",
    title: "CT Technologist",
    category: "Imaging",
    summary: "Computed tomography across ED, inpatient, outpatient.",
    sections: [
      {
        title: "CT Imaging",
        questions: [
          "CT protocol selection and kVp / mA optimization",
          "CTA — head, chest, abdomen, runoff",
          "Trauma pan-scan workflow and critical-findings escalation",
          "Dose-reduction (ALARA) and pediatric protocols",
          "PICC / port access and power-injector setup",
          "Image post-processing and 3D reconstruction",
        ],
      },
      {
        title: "Patient & Safety",
        questions: [
          "Contrast reactions — recognition and treatment",
          "Renal-function / GFR screening pre-contrast",
          "Pregnancy screening and shielding",
          "Patient positioning and immobilization",
          "PACS and worklist QA",
          "ARRT credentialing and CME",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "radiology-technologist",
    title: "Radiology Technologist",
    category: "Imaging",
    summary: "General diagnostic radiologic technologist.",
    sections: [
      {
        title: "General Radiography",
        questions: [
          "Routine radiographic positioning across body regions",
          "Portable / bedside imaging and OR fluoroscopy",
          "C-arm operation and dose management",
          "Pediatric and trauma imaging adaptations",
          "Contrast studies — barium, urography, fistulagram",
          "Image quality, artifact recognition, and PACS QA",
        ],
      },
      {
        title: "Patient Care & Safety",
        questions: [
          "Patient identification and pregnancy screening",
          "Radiation safety and lead PPE",
          "Sterile technique for fluoro procedures",
          "Patient transfers and immobilization",
          "Documentation and image post-processing",
          "ARRT credentialing and CME",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
  {
    slug: "emergency-medical-technician-emt",
    title: "Emergency Medical Technician (EMT)",
    category: "Pre-Hospital",
    summary: "EMT-Basic / Advanced pre-hospital care.",
    sections: [
      {
        title: "Pre-Hospital Care",
        questions: [
          "Primary / secondary patient assessment",
          "Airway management — OPA / NPA / BVM, supraglottic",
          "BLS resuscitation and AED use",
          "Trauma assessment and spinal motion restriction",
          "Bleeding control and tourniquet application",
          "Cardiac and stroke assessment with field destination decisions",
        ],
      },
      {
        title: "Operations",
        questions: [
          "Scene safety and HazMat awareness",
          "Mass-casualty triage (START / SALT)",
          "Communication with medical control",
          "Documentation per state EMS protocols",
          "Vehicle / equipment readiness",
          "Patient-handoff to ED",
        ],
      },
      INFECTION_CONTROL,
    ],
  },
  {
    slug: "medical-technologist",
    title: "Medical Technologist",
    category: "Laboratory",
    summary: "Medical technologist — high-complexity testing.",
    sections: [
      {
        title: "High-Complexity Testing",
        questions: [
          "Chemistry, hematology, coag, and urinalysis benches",
          "Microbiology — plating, MALDI / Vitek, susceptibilities",
          "Blood bank — ABO/Rh, crossmatch, antibody panels",
          "Molecular and infectious-disease assays",
          "Cytogenetics and special-chemistry assays (where applicable)",
          "Method validation and verification",
        ],
      },
      {
        title: "QA & Compliance",
        questions: [
          "QC review and corrective action",
          "Proficiency testing (CAP)",
          "Instrument maintenance and troubleshooting",
          "LIS verification and result release",
          "Critical-value reporting and call-back",
          "CAP / CLIA / Joint Commission readiness",
        ],
      },
      PROFESSIONAL_PRACTICE,
    ],
  },
];

/* ---------- Public registry ---------- */
export const CHECKLISTS = {
  nurse: {
    label: "Nurse Checklists",
    blurb:
      "Validate specialty competency across 50+ nursing modules — from Med-Surg, ICU, OR, L&D, ED, Telemetry, and beyond. Self-rate, attest, and submit for recruiter review.",
    items: nurse,
  },
  therapist: {
    label: "Therapist Checklists",
    blurb:
      "Therapy skills checklists for PT, OT, SLP, and Respiratory Therapy — across acute, inpatient rehab, outpatient, SNF, home, pediatric, and school-based settings.",
    items: therapist,
  },
  other: {
    label: "Other Clinical & Allied Checklists",
    blurb:
      "Allied-health and clinical-support checklists — Medical Assistant, CNA, LPN, Phlebotomy, Sterile Processing, Surgical Technologist, Pharmacy, Imaging, EMT, and more.",
    items: other,
  },
};

export const CHECKLIST_TYPES = ["nurse", "therapist", "other"];

export const RATING_LABELS = {
  1: { label: "No Experience",                  short: "No exp",       dot: "bg-slate-300" },
  2: { label: "Need Training",                  short: "Need training", dot: "bg-amber-400" },
  3: { label: "Able to perform with supervision", short: "Supervised",  dot: "bg-emerald-500" },
  4: { label: "Able to perform independently",   short: "Independent",  dot: "bg-trital-navy" },
};

/** Lookup helper — used by ChecklistForm route */
export function findChecklist(type, slug) {
  const group = CHECKLISTS[type];
  if (!group) return null;
  const item = group.items.find((c) => c.slug === slug);
  if (!item) return null;
  return { ...item, type, typeLabel: group.label };
}
